//frontend/src/components/CompleteProfile.jsx

//frontend/src/components/CompleteProfile.jsx

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Card, Form, Button, Row, Col, Alert, ProgressBar, Tab, Tabs } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import { FaUser, FaPassport, FaBriefcase, FaGraduationCap, FaLanguage, FaPhone } from 'react-icons/fa';
import api from '../services/api';
import { toast } from 'react-toastify';

const CompleteProfile = () => {
    const { user, setUser } = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [completion, setCompletion] = useState(0);
    const [activeTab, setActiveTab] = useState('personal');
    const [errors, setErrors] = useState({});
    const [formData, setFormData] = useState({
        // Informations personnelles
        first_name: user?.first_name || '',
        last_name: user?.last_name || '',
        email: user?.email || '',
        phone: user?.phone || '',
        date_of_birth: '',
        place_of_birth: '',
        gender: '',
        nationality: '',
        marital_status: '',
        
        // Adresse
        address: '',
        city: '',
        postal_code: '',
        country: '',
        
        // Passeport
        passport_number: '',
        passport_issue_date: '',
        passport_expiry_date: '',
        passport_issue_place: '',
        
        // Professionnel
        current_occupation: '',
        employer: '',
        years_of_experience: '',
        monthly_income: '',
        
        // Éducation
        education_level: '',
        field_of_study: '',
        institution: '',
        graduation_year: '',
        
        // Langues
        french_level: '',
        english_level: '',
        other_languages: '',
        
        // Contact d'urgence
        emergency_contact_name: '',
        emergency_contact_phone: '',
        emergency_contact_relationship: '',
    });

    // Charger le profil au montage du composant
    useEffect(() => {
        loadProfile();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Recalculer la completion quand formData change
    useEffect(() => {
        const requiredFields = [
            'first_name', 'last_name', 'email', 'phone', 'date_of_birth',
            'place_of_birth', 'gender', 'nationality', 'address', 'city',
            'country', 'passport_number', 'current_occupation', 'education_level'
        ];

        const filledFields = requiredFields.filter(field => formData[field]);
        const percentage = Math.round((filledFields.length / requiredFields.length) * 100);
        setCompletion(percentage);
    }, [formData]);

    const loadProfile = async () => {
        try {
            const response = await api.get('/user');
            const userData = response.data.user;
            
            // Helper function to format dates for input fields
            const formatDateForInput = (dateString) => {
                if (!dateString) return '';
                const date = new Date(dateString);
                return date.toISOString().split('T')[0];
            };

            setFormData({
                first_name: userData.first_name || '',
                last_name: userData.last_name || '',
                email: userData.email || '',
                phone: userData.phone || '',
                date_of_birth: formatDateForInput(userData.date_of_birth),
                place_of_birth: userData.place_of_birth || '',
                gender: userData.gender || '',
                nationality: userData.nationality || '',
                marital_status: userData.marital_status || '',
                address: userData.address || '',
                city: userData.city || '',
                postal_code: userData.postal_code || '',
                country: userData.country || '',
                passport_number: userData.passport_number || '',
                passport_issue_date: formatDateForInput(userData.passport_issue_date),
                passport_expiry_date: formatDateForInput(userData.passport_expiry_date),
                passport_issue_place: userData.passport_issue_place || '',
                current_occupation: userData.current_occupation || '',
                employer: userData.employer || '',
                years_of_experience: userData.years_of_experience || '',
                monthly_income: userData.monthly_income || '',
                education_level: userData.education_level || '',
                field_of_study: userData.field_of_study || '',
                institution: userData.institution || '',
                graduation_year: userData.graduation_year || '',
                french_level: userData.french_level || '',
                english_level: userData.english_level || '',
                other_languages: userData.other_languages || '',
                emergency_contact_name: userData.emergency_contact_name || '',
                emergency_contact_phone: userData.emergency_contact_phone || '',
                emergency_contact_relationship: userData.emergency_contact_relationship || '',
            });
            
            setCompletion(userData.profile_completion_percentage || 0);
        } catch (error) {
            console.error('Error loading profile:', error);
            toast.error('Erreur lors du chargement du profil');
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value,
        });
        // Clear error for this field when user starts typing
        if (errors[name]) {
            setErrors({
                ...errors,
                [name]: '',
            });
        }
    };

    const validateForm = () => {
        const newErrors = {};
        const requiredFields = [
            { name: 'first_name', tab: 'personal', label: 'Prénom' },
            { name: 'last_name', tab: 'personal', label: 'Nom' },
            { name: 'email', tab: 'personal', label: 'Email' },
            { name: 'phone', tab: 'personal', label: 'Téléphone' },
            { name: 'date_of_birth', tab: 'personal', label: 'Date de naissance' },
            { name: 'place_of_birth', tab: 'personal', label: 'Lieu de naissance' },
            { name: 'gender', tab: 'personal', label: 'Genre' },
            { name: 'nationality', tab: 'personal', label: 'Nationalité' },
            { name: 'address', tab: 'personal', label: 'Adresse' },
            { name: 'city', tab: 'personal', label: 'Ville' },
            { name: 'country', tab: 'personal', label: 'Pays' },
            { name: 'passport_number', tab: 'passport', label: 'Numéro de passeport' },
            { name: 'current_occupation', tab: 'professional', label: 'Profession actuelle' },
            { name: 'education_level', tab: 'education', label: 'Niveau d\'éducation' },
        ];

        requiredFields.forEach(field => {
            if (!formData[field.name] || formData[field.name].trim() === '') {
                newErrors[field.name] = `${field.label} est requis`;
            }
        });

        // Additional validations
        if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Format d\'email invalide';
        }

        if (formData.phone && !/^\+?[\d\s\-\(\)]+$/.test(formData.phone)) {
            newErrors.phone = 'Format de téléphone invalide';
        }

        if (formData.date_of_birth) {
            const birthDate = new Date(formData.date_of_birth);
            const today = new Date();
            const age = today.getFullYear() - birthDate.getFullYear();
            if (age < 18 || age > 100) {
                newErrors.date_of_birth = 'L\'âge doit être entre 18 et 100 ans';
            }
        }

        if (formData.passport_issue_date && formData.passport_expiry_date) {
            const issueDate = new Date(formData.passport_issue_date);
            const expiryDate = new Date(formData.passport_expiry_date);
            if (expiryDate <= issueDate) {
                newErrors.passport_expiry_date = 'La date d\'expiration doit être postérieure à la date de délivrance';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            // Find first tab with errors
            const errorFields = Object.keys(errors);
            const requiredFields = [
                { name: 'first_name', tab: 'personal' },
                { name: 'last_name', tab: 'personal' },
                { name: 'email', tab: 'personal' },
                { name: 'phone', tab: 'personal' },
                { name: 'date_of_birth', tab: 'personal' },
                { name: 'place_of_birth', tab: 'personal' },
                { name: 'gender', tab: 'personal' },
                { name: 'nationality', tab: 'personal' },
                { name: 'address', tab: 'personal' },
                { name: 'city', tab: 'personal' },
                { name: 'country', tab: 'personal' },
                { name: 'passport_number', tab: 'passport' },
                { name: 'current_occupation', tab: 'professional' },
                { name: 'education_level', tab: 'education' },
            ];

            const firstErrorField = errorFields.find(field => requiredFields.find(rf => rf.name === field));
            if (firstErrorField) {
                const tab = requiredFields.find(rf => rf.name === firstErrorField)?.tab;
                if (tab) setActiveTab(tab);
            }

            toast.error('Veuillez corriger les erreurs dans le formulaire');
            return;
        }

        setLoading(true);
        try {
            // Nettoyer les données avant l'envoi
            const cleanedData = { ...formData };
            
            // Convertir les chaînes vides en null pour les champs numériques
            if (cleanedData.years_of_experience === '') {
                cleanedData.years_of_experience = null;
            }
            if (cleanedData.monthly_income === '') {
                cleanedData.monthly_income = null;
            }
            if (cleanedData.graduation_year === '') {
                cleanedData.graduation_year = null;
            }
            
            // Convertir les chaînes vides en null pour les champs de date
            if (cleanedData.passport_issue_date === '') {
                cleanedData.passport_issue_date = null;
            }
            if (cleanedData.passport_expiry_date === '') {
                cleanedData.passport_expiry_date = null;
            }

            // Envoyer les données
            const response = await api.put('/user', cleanedData);

            // Mettre à jour le contexte utilisateur
            if (setUser) {
                setUser(response.data);
            }

            // Recharger le profil pour obtenir le pourcentage mis à jour
            const updatedProfile = await api.get('/user');
            if (setUser) {
                setUser(updatedProfile.data.user);
            }

            // Mettre à jour l'état local
            setCompletion(updatedProfile.data.user.profile_completion_percentage || 0);
            
            toast.success('✅ Profil mis à jour avec succès !');
            
            // Rediriger vers la page profile
            setTimeout(() => {
                navigate('/profile');
            }, 1500);
        } catch (error) {
            console.error('Error updating profile:', error);
            if (error.response?.data) {
                const errors = error.response.data;
                const errorMessages = Object.entries(errors)
                    .map(([field, messages]) => `${field}: ${messages.join(', ')}`)
                    .join('\n');
                toast.error(`Erreurs de validation:\n${errorMessages}`);
            } else {
                toast.error('❌ Erreur lors de la mise à jour du profil');
            }
        } finally {
            setLoading(false);
        }
    };

    const getCompletionColor = () => {
        if (completion < 50) return 'danger';
        if (completion < 80) return 'warning';
        return 'success';
    };

    return (
        <Container className="py-4">
            <Row className="mb-4">
                <Col>
                    <h2>
                        <FaUser className="me-3" />
                        Compléter mon profil
                    </h2>
                    <p className="text-muted">
                        Complétez votre profil pour pouvoir créer votre dossier de demande de visa
                    </p>
                </Col>
            </Row>

            {/* Barre de progression */}
            <Card className="mb-4">
                <Card.Body>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <span className="fw-bold">Complétion du profil</span>
                        <span className="fw-bold">{completion}%</span>
                    </div>
                    <ProgressBar 
                        now={completion} 
                        variant={getCompletionColor()}
                        style={{ height: '10px' }}
                    />
                    {completion < 80 && (
                        <Alert variant="warning" className="mt-3 mb-0">
                            <i className="fas fa-exclamation-triangle me-2"></i>
                            Vous devez compléter au moins 80% de votre profil pour créer un dossier
                        </Alert>
                    )}
                </Card.Body>
            </Card>

            <Form onSubmit={handleSubmit}>
                <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-4">
                    {/* Informations Personnelles */}
                    <Tab eventKey="personal" title={<><FaUser className="me-2" />Personnel</>}>
                        <Card className="mb-4">
                            <Card.Body>
                                <h5 className="mb-4">Informations personnelles</h5>
                                
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Nom *</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="last_name"
                                                value={formData.last_name}
                                                onChange={handleChange}
                                                isInvalid={!!errors.last_name}
                                                required
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {errors.last_name}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Prénoms *</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="first_name"
                                                value={formData.first_name}
                                                onChange={handleChange}
                                                isInvalid={!!errors.first_name}
                                                required
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {errors.first_name}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Date de naissance *</Form.Label>
                                            <Form.Control
                                                type="date"
                                                name="date_of_birth"
                                                value={formData.date_of_birth}
                                                onChange={handleChange}
                                                isInvalid={!!errors.date_of_birth}
                                                required
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {errors.date_of_birth}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Lieu de naissance *</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="place_of_birth"
                                                value={formData.place_of_birth}
                                                onChange={handleChange}
                                                isInvalid={!!errors.place_of_birth}
                                                required
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {errors.place_of_birth}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col md={4}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Genre *</Form.Label>
                                            <Form.Select
                                                name="gender"
                                                value={formData.gender}
                                                onChange={handleChange}
                                                isInvalid={!!errors.gender}
                                                required
                                            >
                                                <option value="">Sélectionner</option>
                                                <option value="M">Masculin</option>
                                                <option value="F">Féminin</option>
                                                <option value="O">Autre</option>
                                            </Form.Select>
                                            <Form.Control.Feedback type="invalid">
                                                {errors.gender}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Nationalité *</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="nationality"
                                                value={formData.nationality}
                                                onChange={handleChange}
                                                isInvalid={!!errors.nationality}
                                                required
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {errors.nationality}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>État civil</Form.Label>
                                            <Form.Select
                                                name="marital_status"
                                                value={formData.marital_status}
                                                onChange={handleChange}
                                            >
                                                <option value="">Sélectionner</option>
                                                <option value="single">Célibataire</option>
                                                <option value="married">Marié(e)</option>
                                                <option value="divorced">Divorcé(e)</option>
                                                <option value="widowed">Veuf/Veuve</option>
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Email *</Form.Label>
                                            <Form.Control
                                                type="email"
                                                name="email"
                                                value={formData.email}
                                                onChange={handleChange}
                                                isInvalid={!!errors.email}
                                                required
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {errors.email}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Téléphone *</Form.Label>
                                            <Form.Control
                                                type="tel"
                                                name="phone"
                                                value={formData.phone}
                                                onChange={handleChange}
                                                isInvalid={!!errors.phone}
                                                required
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {errors.phone}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <h6 className="mt-4 mb-3">Adresse</h6>
                                <Form.Group className="mb-3">
                                    <Form.Label>Adresse complète *</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={2}
                                        name="address"
                                        value={formData.address}
                                        onChange={handleChange}
                                        isInvalid={!!errors.address}
                                        required
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.address}
                                    </Form.Control.Feedback>
                                </Form.Group>

                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Ville *</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="city"
                                                value={formData.city}
                                                onChange={handleChange}
                                                isInvalid={!!errors.city}
                                                required
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {errors.city}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Code postal</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="postal_code"
                                                value={formData.postal_code}
                                                onChange={handleChange}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Pays *</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="country"
                                                value={formData.country}
                                                onChange={handleChange}
                                                isInvalid={!!errors.country}
                                                required
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {errors.country}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    </Tab>

                    {/* Passeport */}
                    <Tab eventKey="passport" title={<><FaPassport className="me-2" />Passeport</>}>
                        <Card className="mb-4">
                            <Card.Body>
                                <h5 className="mb-4">Informations passeport</h5>
                                
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Numéro de passeport *</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="passport_number"
                                                value={formData.passport_number}
                                                onChange={handleChange}
                                                isInvalid={!!errors.passport_number}
                                                required
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {errors.passport_number}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Lieu de délivrance</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="passport_issue_place"
                                                value={formData.passport_issue_place}
                                                onChange={handleChange}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Date de délivrance</Form.Label>
                                            <Form.Control
                                                type="date"
                                                name="passport_issue_date"
                                                value={formData.passport_issue_date}
                                                onChange={handleChange}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Date d'expiration</Form.Label>
                                            <Form.Control
                                                type="date"
                                                name="passport_expiry_date"
                                                value={formData.passport_expiry_date}
                                                onChange={handleChange}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    </Tab>

                    {/* Professionnel */}
                    <Tab eventKey="professional" title={<><FaBriefcase className="me-2" />Professionnel</>}>
                        <Card className="mb-4">
                            <Card.Body>
                                <h5 className="mb-4">Informations professionnelles</h5>
                                
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Profession actuelle *</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="current_occupation"
                                                value={formData.current_occupation}
                                                onChange={handleChange}
                                                isInvalid={!!errors.current_occupation}
                                                required
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {errors.current_occupation}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Employeur</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="employer"
                                                value={formData.employer}
                                                onChange={handleChange}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Années d'expérience</Form.Label>
                                            <Form.Control
                                                type="number"
                                                name="years_of_experience"
                                                value={formData.years_of_experience}
                                                onChange={handleChange}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Revenu mensuel (€)</Form.Label>
                                            <Form.Control
                                                type="number"
                                                name="monthly_income"
                                                value={formData.monthly_income}
                                                onChange={handleChange}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    </Tab>

                    {/* Éducation */}
                    <Tab eventKey="education" title={<><FaGraduationCap className="me-2" />Éducation</>}>
                        <Card className="mb-4">
                            <Card.Body>
                                <h5 className="mb-4">Formation académique</h5>
                                
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Niveau d'études *</Form.Label>
                                            <Form.Select
                                                name="education_level"
                                                value={formData.education_level}
                                                onChange={handleChange}
                                                isInvalid={!!errors.education_level}
                                                required
                                            >
                                                <option value="">Sélectionner</option>
                                                <option value="high_school">Lycée</option>
                                                <option value="bachelor">Licence/Bachelor</option>
                                                <option value="master">Master</option>
                                                <option value="doctorate">Doctorat</option>
                                                <option value="other">Autre</option>
                                            </Form.Select>
                                            <Form.Control.Feedback type="invalid">
                                                {errors.education_level}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Domaine d'études</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="field_of_study"
                                                value={formData.field_of_study}
                                                onChange={handleChange}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col md={8}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Institution/Université</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="institution"
                                                value={formData.institution}
                                                onChange={handleChange}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Année d'obtention</Form.Label>
                                            <Form.Control
                                                type="number"
                                                name="graduation_year"
                                                value={formData.graduation_year}
                                                onChange={handleChange}
                                                min="1950"
                                                max={new Date().getFullYear()}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    </Tab>

                    {/* Langues */}
                    <Tab eventKey="languages" title={<><FaLanguage className="me-2" />Langues</>}>
                        <Card className="mb-4">
                            <Card.Body>
                                <h5 className="mb-4">Compétences linguistiques</h5>
                                
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Niveau de français</Form.Label>
                                            <Form.Select
                                                name="french_level"
                                                value={formData.french_level}
                                                onChange={handleChange}
                                            >
                                                <option value="">Sélectionner</option>
                                                <option value="A1">A1 - Débutant</option>
                                                <option value="A2">A2 - Élémentaire</option>
                                                <option value="B1">B1 - Intermédiaire</option>
                                                <option value="B2">B2 - Intermédiaire avancé</option>
                                                <option value="C1">C1 - Avancé</option>
                                                <option value="C2">C2 - Maîtrise</option>
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Niveau d'anglais</Form.Label>
                                            <Form.Select
                                                name="english_level"
                                                value={formData.english_level}
                                                onChange={handleChange}
                                            >
                                                <option value="">Sélectionner</option>
                                                <option value="A1">A1 - Débutant</option>
                                                <option value="A2">A2 - Élémentaire</option>
                                                <option value="B1">B1 - Intermédiaire</option>
                                                <option value="B2">B2 - Intermédiaire avancé</option>
                                                <option value="C1">C1 - Avancé</option>
                                                <option value="C2">C2 - Maîtrise</option>
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Form.Group className="mb-3">
                                    <Form.Label>Autres langues</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={2}
                                        name="other_languages"
                                        value={formData.other_languages}
                                        onChange={handleChange}
                                        placeholder="Ex: Espagnol (B1), Allemand (A2)"
                                    />
                                </Form.Group>
                            </Card.Body>
                        </Card>
                    </Tab>

                    {/* Contact d'urgence */}
                    <Tab eventKey="emergency" title={<><FaPhone className="me-2" />Urgence</>}>
                        <Card className="mb-4">
                            <Card.Body>
                                <h5 className="mb-4">Contact d'urgence</h5>
                                
                                <Form.Group className="mb-3">
                                    <Form.Label>Nom complet</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="emergency_contact_name"
                                        value={formData.emergency_contact_name}
                                        onChange={handleChange}
                                    />
                                </Form.Group>

                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Téléphone</Form.Label>
                                            <Form.Control
                                                type="tel"
                                                name="emergency_contact_phone"
                                                value={formData.emergency_contact_phone}
                                                onChange={handleChange}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Relation</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="emergency_contact_relationship"
                                                value={formData.emergency_contact_relationship}
                                                onChange={handleChange}
                                                placeholder="Ex: Père, Mère, Conjoint(e)"
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    </Tab>
                </Tabs>

                <div className="d-flex justify-content-end gap-3">
                    <Button variant="secondary" onClick={() => window.history.back()}>
                        Annuler
                    </Button>
                    <Button type="submit" variant="primary" disabled={loading}>
                        {loading ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2"></span>
                                Enregistrement...
                            </>
                        ) : (
                            'Enregistrer le profil'
                        )}
                    </Button>
                </div>
            </Form>
        </Container>
    );
};

export default CompleteProfile;